/**
 * ImportDesignImageDialog
 *
 * Shared dialog for importing an image into an editable Space design.
 *
 * Primary responsibilities:
 * - Collect image file and optional design metadata
 * - Enforce plan gating for new design creation
 * - Emit a normalized import payload on submit
 *
 * Dependencies:
 * - Shared dialog and form controls
 * - Space subscription helpers
 */

"use client"

import {
  ChangeEvent,
  FC,
  useContext,
  useEffect,
  useRef,
  useState
} from "react"
import { PlanTierBadge } from "@/components/billing/plan-tier-badge"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { ChatbotUIContext } from "@/context/context"
import {
  getRequiredSpaceObjectPlanId,
  getSpaceObjectUpgradeMessage,
  scheduleDelayedPaywall
} from "@/lib/subscription"
import {
  canCreateSpaceObject,
  type LosiPublicPlanId
} from "@/lib/billing/losi-pricing"
import {
  getAnalyticsPlanId,
  ProductAnalyticsEventNames,
  trackProductEvent
} from "@/lib/analytics/product-events"
import { toast } from "sonner"
import { ImagePlus, Sparkles } from "lucide-react"

export interface ImportDesignImageDialogResult {
  description: string | null
  file: File
  title: string
}

export interface ImportDesignImageDialogProps {
  defaultDescription?: string | null
  defaultTitle?: string
  isOpen: boolean
  mode?: "create" | "replace"
  onClose: () => void
  onImport: (payload: ImportDesignImageDialogResult) => void | Promise<void>
  submitLabel?: string
  spaceId?: string | null
  effectivePlanId?: LosiPublicPlanId | null
  workspacePlanId?: LosiPublicPlanId | null
  workspaceId?: string | null
}

type SelectedImageMeta = {
  previewUrl: string
  width: number
  height: number
}

const readImageDimensions = async (file: File) => {
  const previewUrl = URL.createObjectURL(file)

  try {
    const image = document.createElement("img")

    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve()
      image.onerror = () => reject(new Error("Failed to read image preview"))
      image.src = previewUrl
    })

    return {
      previewUrl,
      width: image.naturalWidth,
      height: image.naturalHeight
    }
  } catch (error) {
    URL.revokeObjectURL(previewUrl)
    throw error
  }
}

export const ImportDesignImageDialog: FC<ImportDesignImageDialogProps> = ({
  defaultDescription = "",
  defaultTitle = "",
  isOpen,
  mode = "create",
  onClose,
  onImport,
  submitLabel,
  spaceId = null,
  effectivePlanId = null,
  workspacePlanId = null,
  workspaceId = null
}) => {
  const { profile, setIsPaywallOpen } = useContext(ChatbotUIContext)
  const [title, setTitle] = useState(defaultTitle)
  const [description, setDescription] = useState(defaultDescription || "")
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [selectedImageMeta, setSelectedImageMeta] =
    useState<SelectedImageMeta | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const paywallTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null)
  const currentPlan = getAnalyticsPlanId(profile)
  const effectivePlan = effectivePlanId ?? currentPlan
  const workspacePlan = workspacePlanId ?? effectivePlan
  const canCreateDesign = Boolean(
    profile && canCreateSpaceObject(effectivePlan, "design")
  )
  const showTierBadge =
    mode === "create" && !canCreateDesign

  useEffect(() => {
    return () => {
      if (paywallTimerRef.current) {
        clearTimeout(paywallTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    return () => {
      if (selectedImageMeta?.previewUrl) {
        URL.revokeObjectURL(selectedImageMeta.previewUrl)
      }
    }
  }, [selectedImageMeta])

  const resetState = () => {
    if (selectedImageMeta?.previewUrl) {
      URL.revokeObjectURL(selectedImageMeta.previewUrl)
    }

    setTitle(defaultTitle)
    setDescription(defaultDescription || "")
    setSelectedFile(null)
    setSelectedImageMeta(null)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      return
    }

    if (!isSubmitting) {
      resetState()
      onClose()
    }
  }

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] || null

    if (!nextFile) {
      setSelectedFile(null)
      setSelectedImageMeta(null)
      return
    }

    if (!nextFile.type.startsWith("image/")) {
      toast.error("Please choose an image file")
      event.target.value = ""
      return
    }

    try {
      const nextMeta = await readImageDimensions(nextFile)

      if (selectedImageMeta?.previewUrl) {
        URL.revokeObjectURL(selectedImageMeta.previewUrl)
      }

      setSelectedFile(nextFile)
      setSelectedImageMeta(nextMeta)

      if (!title.trim()) {
        setTitle(
          nextFile.name
            .replace(/\.[^.]+$/, "")
            .replace(/[-_]+/g, " ")
            .trim()
        )
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to read image file"
      toast.error(message)
    }
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!profile?.user_id) {
      toast.error("You must be logged in to import designs")
      return
    }

    if (mode === "create" && !canCreateDesign) {
      void trackProductEvent(ProductAnalyticsEventNames.SPACE_OBJECT_GATE_CLICKED, {
        surface: "spaces",
        source: "import_design_image_dialog",
        current_plan: currentPlan,
        effective_plan: effectivePlan,
        workspace_plan: workspacePlan,
        workspace_id: workspaceId ?? undefined,
        required_plan: getRequiredSpaceObjectPlanId("design"),
        locked_object_type: "design",
        item_type: "design",
        space_id: spaceId ?? undefined,
        trigger: "locked_feature_click"
      })
      void trackProductEvent(ProductAnalyticsEventNames.UPGRADE_PROMPT_VIEWED, {
        surface: "spaces",
        source: "import_design_image_dialog",
        current_plan: currentPlan,
        effective_plan: effectivePlan,
        workspace_plan: workspacePlan,
        workspace_id: workspaceId ?? undefined,
        recommended_plan: getRequiredSpaceObjectPlanId("design"),
        required_plan: getRequiredSpaceObjectPlanId("design"),
        tier_suggested: getRequiredSpaceObjectPlanId("design"),
        prompt_type: "hard_gate",
        object_type: "design",
        fallback_offered: "save_as_note",
        trigger: "locked_feature_click",
        space_id: spaceId ?? undefined,
        item_type: "design"
      })
      toast.error(getSpaceObjectUpgradeMessage(profile, "design"))

      if (setIsPaywallOpen) {
        if (paywallTimerRef.current) {
          clearTimeout(paywallTimerRef.current)
        }

        paywallTimerRef.current = scheduleDelayedPaywall(() => {
          setIsPaywallOpen(true)
        })
      }

      return
    }

    if (!selectedFile) {
      toast.error("Choose an image to import")
      return
    }

    setIsSubmitting(true)

    try {
      await onImport({
        title: title.trim() || "Imported Design",
        description: description.trim().length > 0 ? description.trim() : null,
        file: selectedFile
      })

      resetState()
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-dvh overflow-y-auto p-4 sm:max-w-[600px] sm:p-6">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <DialogTitle className="flex items-center gap-2">
                  <ImagePlus className="size-4 text-fuchsia-600" />
                  {mode === "replace" ? "Replace With Imported Design" : "Import Image"}
                </DialogTitle>
                <DialogDescription>
                  Turn an image into editable design layers with text, shapes,
                  and images you can refine.
                </DialogDescription>
              </div>
              {showTierBadge ? <PlanTierBadge planId="pro" /> : null}
            </div>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="design-import-title">Title</Label>
              <Input
                id="design-import-title"
                value={title}
                onChange={event => setTitle(event.target.value)}
                placeholder="Campaign poster reconstruction"
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="design-import-description">Description</Label>
              <Textarea
                id="design-import-description"
                rows={3}
                value={description}
                onChange={event => setDescription(event.target.value)}
                placeholder="Optional context for this imported design"
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-3">
              <Label htmlFor="design-import-file">Source image</Label>
              <Input
                id="design-import-file"
                type="file"
                accept="image/*"
                disabled={isSubmitting}
                onChange={event => {
                  void handleFileChange(event)
                }}
              />

              <div className="bg-muted/20 rounded-3xl border p-4">
                {selectedImageMeta ? (
                  <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_180px]">
                    <div className="space-y-2">
                      <p className="text-sm font-medium">{selectedFile?.name}</p>
                      <p className="text-muted-foreground text-xs">
                        {selectedImageMeta.width} x {selectedImageMeta.height} px
                      </p>
                      <div className="flex items-start gap-2 rounded-2xl border bg-fuchsia-50/70 p-3 text-xs text-fuchsia-900 dark:border-fuchsia-950/60 dark:bg-fuchsia-950/20 dark:text-fuchsia-100">
                        <Sparkles className="mt-0.5 size-3.5 shrink-0" />
                        <span>
                          Losi keeps detected text editable and preserves likely
                          font styling when the image makes it clear.
                        </span>
                      </div>
                    </div>

                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={selectedImageMeta.previewUrl}
                      alt="Selected design source"
                      className="h-36 w-full rounded-2xl border object-cover"
                    />
                  </div>
                ) : (
                  <div className="flex min-h-[144px] flex-col items-center justify-center gap-2 text-center">
                    <div className="flex size-12 items-center justify-center rounded-2xl bg-fuchsia-50 text-fuchsia-600 dark:bg-fuchsia-950/40 dark:text-fuchsia-300">
                      <ImagePlus className="size-5" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium">
                        Choose an image to reconstruct
                      </p>
                      <p className="text-muted-foreground text-xs">
                        PNG, JPG, WEBP, or HEIC are supported.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !selectedFile}
            >
              {isSubmitting
                ? mode === "replace"
                  ? "Rebuilding..."
                  : "Importing..."
                : submitLabel ||
                  (mode === "replace"
                    ? "Replace With Import"
                    : "Import As Editable Design")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default ImportDesignImageDialog
